import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import './ETPOSDashboard.css';
import apiService from './services/api';
import dateUtils from './utils/dateUtils';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Home from './components/ETPOS/Home';
import Users from './components/ETPOS/Users';
import Contacts from './components/ETPOS/Contacts';
import Products from './components/ETPOS/Products';
import Purchases from './components/ETPOS/Purchases';
import ListPurchases from './components/ETPOS/ListPurchases';
import PurchaseRequisition from './components/ETPOS/PurchaseRequisition';
import PurchaseOrder from './components/ETPOS/PurchaseOrder';
import ListPurchaseReturn from './components/ETPOS/ListPurchaseReturn';
import AddPurchase from './components/ETPOS/AddPurchase';
import Sell from './components/Sell';
import StockTransfers from './components/ETPOS/StockTransfers';
import StockAdjustment from './components/ETPOS/StockAdjustment';
import Expenses from './components/ETPOS/Expenses';
import PaymentAccounts from './components/ETPOS/PaymentAccounts';
import Accounting from './components/ETPOS/Accounting';
import Reports from './components/ETPOS/Reports';
import NotificationTemplates from './components/ETPOS/NotificationTemplates';
import Settings from './components/ETPOS/Settings';
import CRM from './components/ETPOS/CRM';
import WoocommerceConnector from './components/ETPOS/WoocommerceConnector';
import PlaidConnector from './components/ETPOS/PlaidConnector';
import Calculator from './components/Calculator';
import TodayProfitModal from './components/Modals/TodayProfitModal';
// SuperAdmin components
import SystemSettings from './components/ETPOS/SystemSettings';
import UserRoles from './components/ETPOS/UserRoles';
import BusinessManagement from './components/ETPOS/BusinessManagement';
import SystemMonitoring from './components/ETPOS/SystemMonitoring';
import AuditLogs from './components/ETPOS/AuditLogs';
import BackupRestore from './components/ETPOS/BackupRestore';
import { usePermissionContext } from './contexts/PermissionContext';
import SecureAIAssistant from './components/AI/SecureAIAssistant';

const ETPOSDashboard = () => {
  const { hasPermission, hasAnyPermission, isLoading: permissionLoading } = usePermissionContext();

  const [activeTab, setActiveTab] = useState(() => {
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(p => p);
    const firstPath = pathParts[0];

    // Handle specific routes
    if (path.startsWith('/sell') || path === '/sell') return 'sell';
    if (firstPath === 'users') return 'users';
    if (firstPath) return firstPath;

    return 'home';
  });
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Close sidebar by default on mobile devices
    return window.innerWidth > 1024;
  });
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [todoDropdownOpen, setTodoDropdownOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showCalculatorPopup, setShowCalculatorPopup] = useState(false);
  const [showTodayProfitModal, setShowTodayProfitModal] = useState(false);
  const [todayProfitData, setTodayProfitData] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('etpos_dark_mode') === 'true');
  const [currentUser, setCurrentUser] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({ purchases: true, reports: true, users: false });
  const userSectionRef = useRef(null);
  const todoDropdownRef = useRef(null);
  const calendarRef = useRef(null);
  const calculatorRef = useRef(null);
  const todayProfitRef = useRef(null);
  const contentWrapperRef = useRef(null);

  const [hoveredItem, setHoveredItem] = useState(null);
  const [clickedMenuId, setClickedMenuId] = useState(null);

  // Close active submenus on mobile click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.etpos-sidebar')) {
        setClickedMenuId(null);
      }
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  // Apply dark mode class to root element
  useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('etpos_dark_mode', darkMode);
  }, [darkMode]);

  const handleLogout = () => {
    // Clear all authentication state
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('etpos_token');
    localStorage.removeItem('etpos_token_expiry');
    localStorage.removeItem('etpos_user_permissions'); // Clear cached permissions

    // Clear session storage as well
    sessionStorage.clear();

    // Navigate to login page with replace to prevent back navigation
    navigate('/login', { replace: true });

    // Clear browser history to prevent back button access
    window.history.pushState(null, '', '/login');
  };

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userSectionRef.current && !userSectionRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
      if (todoDropdownRef.current && !todoDropdownRef.current.contains(event.target)) {
        setTodoDropdownOpen(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
      if (calculatorRef.current && !calculatorRef.current.contains(event.target)) {
        setShowCalculatorPopup(false);
      }
      if (todayProfitRef.current && !todayProfitRef.current.contains(event.target)) {
        setShowTodayProfitModal(false);
      }
    };

    if (userDropdownOpen || todoDropdownOpen || showCalendar || showTodayProfitModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen, todoDropdownOpen, showCalendar, showCalculatorPopup, showTodayProfitModal]);

  // Fetch current user details and business info on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      // Only fetch if user is authenticated
      if (!apiService.isTokenValid()) {
        console.log('User not authenticated, skipping user data fetch');
        return;
      }

      try {
        // Get user details
        const userDetails = await apiService.getUserDetails();

        // Get business locations to get business info
        try {
          const locationsData = await apiService.getBusinessLocations();

          // Extract business info from locations
          let businessInfo = null;
          if (locationsData?.data && locationsData.data.length > 0) {
            businessInfo = {
              name: locationsData.data[0]?.name || 'ET-POS Store',
              location_id: locationsData.data[0]?.id
            };
          } else if (Array.isArray(locationsData) && locationsData.length > 0) {
            businessInfo = {
              name: locationsData[0]?.name || 'ET-POS Store',
              location_id: locationsData[0]?.id
            };
          }

          // Fetch user roles separately
          let userRoles = [];
          try {
            const rolesData = await apiService.getUserRoles();
            userRoles = rolesData.data || [];
            console.log('🔐 USER ROLES - Fetched user roles:', userRoles);
          } catch (rolesError) {
            console.error('Error fetching user roles:', rolesError);
          }

          // Combine user, business info, and roles
          const combinedData = {
            ...userDetails.data,  // Extract user data from the data property
            business: businessInfo,
            roles: userRoles  // Add roles to user data
          };
          console.log('🔐 USER ROLES - Combined user data with roles:', combinedData);
          setCurrentUser(combinedData);

        } catch (locationError) {
          console.error('Error fetching business locations:', locationError);
          // Still set user data even if business fetch fails
          setCurrentUser(userDetails.data || userDetails);
        }

      } catch (error) {
        console.error('Error fetching current user:', error);
        // Don't show error to user if it's just authentication issue
        if (!error.message.includes('authentication')) {
          // Fallback to default if API fails
          const fallbackUser = {
            first_name: 'User',
            last_name: '',
            username: 'user'
          };
          setCurrentUser(fallbackUser);
        }
      }
    };

    fetchUserData();
  }, []);

  // Fetch today's profit data
  const fetchTodayProfitData = async () => {
    try {
      // Get today's date range
      const today = dateUtils.getChicagoToday();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      // Format dates for API
      const startDate = startOfDay.toISOString().split('T')[0];
      const endDate = endOfDay.toISOString().split('T')[0];

      // Fetch sales data for today
      const salesResponse = await apiService.getSellData({
        start_date: startDate,
        end_date: endDate
      });

      // Calculate profit from sales data
      let totalRevenue = 0;
      let totalCost = 0;
      let totalProfit = 0;
      const profitItems = [];

      if (salesResponse?.data && Array.isArray(salesResponse.data)) {
        salesResponse.data.forEach(sale => {
          const revenue = parseFloat(sale.final_total) || 0;
          const cost = parseFloat(sale.total_cost) || 0;
          const profit = revenue - cost;

          totalRevenue += revenue;
          totalCost += cost;
          totalProfit += profit;

          profitItems.push({
            id: sale.id,
            invoiceNo: sale.invoice_no,
            customer: sale.customer?.name || 'Walk-in Customer',
            revenue: revenue,
            cost: cost,
            profit: profit,
            time: dateUtils.formatTime(new Date(sale.transaction_date))
          });
        });
      }

      setTodayProfitData({
        date: dateUtils.formatDate(today),
        totalRevenue,
        totalCost,
        totalProfit,
        items: profitItems,
        totalTransactions: profitItems.length
      });
    } catch (error) {
      console.error('Error fetching today profit data:', error);
      // Set default data on error
      setTodayProfitData({
        date: dateUtils.formatDate(new Date()),
        totalRevenue: 0,
        totalCost: 0,
        totalProfit: 0,
        items: [],
        totalTransactions: 0,
        error: 'Failed to load profit data'
      });
    }
  };

  const menuCategories = [
    {
      title: 'Main',
      items: [
        { id: 'system-settings', label: 'Superadmin', icon: '👑', permission: 'superadmin.access' },
        { id: 'home', label: 'Home', icon: '🏠', permission: null },
        {
          id: 'users',
          label: 'User Management',
          icon: '👥',
          permission: 'user.view',
          subItems: [
            { id: 'users', label: 'Users', icon: 'fa fa-user', permission: 'user.view' },
            { id: 'roles', label: 'Roles', icon: 'fa fa-key', permission: 'roles.view' },
            { id: 'agents', label: 'Sales Commission Agents', icon: 'fa fa-briefcase', permission: 'user.create' },
          ]
        },
        {
          id: 'contacts',
          label: 'Contacts',
          icon: '📇',
          permission: ['supplier.view', 'customer.view', 'supplier.view_own', 'customer.view_own'],
          subItems: [
            { id: 'suppliers', label: 'Suppliers', icon: 'fa fa-truck', permission: ['supplier.view', 'supplier.view_own'] },
            { id: 'customers', label: 'Customers', icon: 'fa fa-users', permission: ['customer.view', 'customer.view_own'] },
            { id: 'customer_groups', label: 'Customer Groups', icon: 'fa fa-layer-group', permission: ['customer.view', 'customer.view_own'] },
            { id: 'import_contacts', label: 'Import Contacts', icon: 'fa fa-upload', permission: ['supplier.create', 'customer.create'] },
          ]
        },
        {
          id: 'sell',
          label: 'Sell',
          icon: '💰',
          permission: ['sell.view', 'sell.create', 'direct_sell.access', 'view_own_sell_only', 'view_commission_agent_sell', 'access_shipping', 'access_own_shipping', 'access_commission_agent_shipping', 'access_sell_return', 'direct_sell.view', 'direct_sell.update', 'access_own_sell_return'],
          subItems: [
            { id: 'all_sales', label: 'All Sales', icon: 'fa fa-list', permission: ['sell.view', 'sell.create', 'direct_sell.access', 'direct_sell.view', 'view_own_sell_only', 'view_commission_agent_sell', 'access_shipping', 'access_own_shipping', 'access_commission_agent_shipping'] },
            { id: 'sales_returns', label: 'Sales Returns', icon: 'fa fa-undo', permission: ['access_sell_return', 'access_own_sell_return'] },
            { id: 'shipment', label: 'Shipment', icon: 'fa fa-truck', permission: ['access_shipping', 'access_own_shipping', 'access_commission_agent_shipping'] },
            { id: 'discount', label: 'Discount', icon: 'fa fa-percent', permission: 'discount.access' },
            { id: 'list_quotation', label: 'List Quotation', icon: 'fa fa-file-text', permission: ['quotation.view_all', 'quotation.view_own'] },
            { id: 'list_draft', label: 'List Draft', icon: 'fa fa-edit', permission: ['draft.view_all', 'draft.view_own'] },
          ]
        },
        {
          id: 'products',
          label: 'Products',
          icon: '📦',
          permission: 'product.view',
          subItems: [
            { id: 'all_products', label: 'All Products', icon: 'fa fa-cube', permission: 'product.view' },
            { id: 'stock_report', label: 'Stock Report', icon: 'fa fa-database', permission: 'product.view' },
            { id: 'units', label: 'Units', icon: 'fa fa-balance-scale', permission: 'product.view' },
            { id: 'warranties', label: 'Warranties', icon: 'fa fa-shield', permission: 'product.view' },
            { id: 'brands', label: 'Brands', icon: 'fa fa-copyright', permission: 'product.view' },
            { id: 'update_price', label: 'Update Price', icon: 'fa fa-tag', permission: 'product.view' },
            { id: 'variation', label: 'Variation', icon: 'fa fa-th-large', permission: 'product.view' },
            { id: 'import_product', label: 'Import Products', icon: 'fa fa-upload', permission: 'product.view' },
            { id: 'categories', label: 'Categories', icon: 'fa fa-list', permission: 'product.view' },
            { id: 'selling_price_group', label: 'Selling Price Group', icon: 'fa fa-dollar-sign', permission: 'product.view' },
          ]
        },
        {
          id: 'purchases',
          label: 'Purchases',
          icon: '🛒',
          permission: ['purchase.view', 'purchase.create', 'purchase.update'],
          subItems: [
            { id: 'list_purchases', label: 'List Purchases', icon: 'fa fa-list', permission: ['purchase.view', 'view_own_purchase'] },
            { id: 'purchase_order', label: 'Purchase Order', icon: 'fa fa-shopping-cart', permission: ['purchase_order.view_all', 'purchase_order.view_own'] },
            { id: 'purchase_requisition', label: 'Purchase Requisition', icon: 'fa fa-file-text', permission: ['purchase_requisition.view_all', 'purchase_requisition.view_own'] },
            { id: 'purchase_return', label: 'List Purchase Return', icon: 'fa fa-undo', permission: 'purchase.update' },
          ]
        },
      ]
    },
    {
      title: 'Transactions',
      items: [
        {
          id: 'stock-transfers',
          label: 'Stock Transfers',
          icon: '🔄',
          permission: ['purchase.view', 'purchase.create'],
          subItems: [
            { id: 'list_transfers', label: 'List Stock Transfers', icon: 'fa fa-list', permission: 'purchase.view' },
            { id: 'add_transfer', label: 'Add Stock Transfer', icon: 'fa fa-plus', permission: 'purchase.create' },
          ]
        },
        {
          id: 'stock-adjustment',
          label: 'Stock Adjustment',
          icon: '⚖️',
          permission: ['purchase.view', 'purchase.create'],
          subItems: [
            { id: 'list_stock_adjustment', label: 'List Stock Adjustment', icon: 'fa fa-list', permission: 'purchase.view' },
            { id: 'add_stock_adjustment', label: 'Add Stock Adjustment', icon: 'fa fa-plus', permission: 'purchase.create' },
          ]
        },
      ]
    },
    {
      title: 'Financial',
      items: [
        {
          id: 'expenses',
          label: 'Expenses',
          icon: '💸',
          permission: ['all_expense.access', 'view_own_expense'],
          subItems: [
            { id: 'list_expenses', label: 'List Expenses', icon: 'fa fa-list', permission: ['all_expense.access', 'view_own_expense'] },
            { id: 'expense_categories', label: 'Expense Categories', icon: 'fa fa-tags', permission: ['expense.add', 'expense.edit'] },
          ]
        },
        {
          id: 'payment-accounts',
          label: 'Payment Accounts',
          icon: '💳',
          permission: 'account.access',
          subItems: [
            { id: 'list_accounts', label: 'List Accounts', icon: 'fa fa-list', permission: 'payment_account.view' },
            { id: 'account_types', label: 'Account Types', icon: 'fa fa-tags', permission: 'payment_account.view' },
            { id: 'balance_sheet', label: 'Balance Sheet', icon: 'fa fa-file-invoice-dollar', permission: 'payment_account.view' },
            { id: 'trial_balance', label: 'Trial Balance', icon: 'fa fa-balance-scale', permission: 'payment_account.view' },
            { id: 'cash_flow', label: 'Cash Flow', icon: 'fa fa-exchange-alt', permission: 'payment_account.view' },
            { id: 'payment_report', label: 'Payment Report', icon: 'fa fa-chart-line', permission: 'account.access' },
          ]
        },
        { id: 'accounting', label: 'Accounting', icon: '📊', permission: 'accounting.access_accounting_module' },
        { id: 'reports', label: 'Reports', icon: '📈', permission: ['purchase_n_sell_report.view', 'contacts_report.view', 'stock_report.view', 'tax_report.view', 'trending_product_report.view', 'sales_representative.view', 'register_report.view', 'expense_report.view'] },
      ]
    },
    {
      title: 'System',
      items: [
        { id: 'notification-templates', label: 'Notification Templates', icon: '📧', permission: 'send_notifications' },
        { id: 'settings', label: 'Settings', icon: '⚙️', permission: ['business_settings.access', 'barcode_settings.access', 'invoice_settings.access', 'tax_rate.view', 'tax_rate.create', 'access_package_subscriptions'] },
        {
          id: 'crm',
          label: 'CRM',
          icon: '🤝',
          permission: ['crm.access_all_schedule', 'crm.access_own_schedule'],
          subItems: [
            { id: 'crm_dashboard', label: 'CRM', icon: 'fa fa-dashboard', permission: ['crm.access_all_schedule', 'crm.access_own_schedule'] },
            { id: 'crm_leads', label: 'Leads', icon: 'fa fa-user-plus', permission: ['crm.access_all_leads', 'crm.access_own_leads'] },
            { id: 'crm_followups', label: 'Follow ups', icon: 'fa fa-phone', permission: ['crm.access_all_schedule', 'crm.access_own_schedule'] },
            { id: 'crm_campaigns', label: 'Campaigns', icon: 'fa fa-bullhorn', permission: ['crm.access_all_campaigns', 'crm.access_own_campaigns'] },
            { id: 'crm_contactslogin', label: 'Contacts Login', icon: 'fa fa-key', permission: 'crm.access_contact_login' },
            { id: 'crm_commissions', label: 'Commissions', icon: 'fa fa-money-bill', permission: ['crm.access_all_schedule', 'crm.access_own_schedule'] },
            { id: 'crm_reports', label: 'Reports', icon: 'fa fa-chart-bar', permission: ['crm.access_all_schedule', 'crm.access_own_schedule'] },
            { id: 'crm_proposal_template', label: 'Proposal template', icon: 'fa fa-file-alt', permission: 'crm.access_proposal' },
            { id: 'crm_proposals', label: 'Proposals', icon: 'fa fa-paper-plane', permission: 'crm.access_proposal' },
            { id: 'crm_sources', label: 'Sources', icon: 'fa fa-link', permission: 'crm.access_sources' },
            { id: 'crm_life_stage', label: 'Life Stage', icon: 'fa fa-heart', permission: 'crm.access_life_stage' },
            { id: 'crm_followup_category', label: 'Followup Category', icon: 'fa fa-tags', permission: ['crm.access_all_schedule', 'crm.access_own_schedule'] },
            { id: 'crm_settings', label: 'Settings', icon: 'fa fa-cog', permission: ['crm.access_all_schedule', 'crm.access_own_schedule'] }
          ]
        },
        { id: 'woocommerce', label: 'Woocommerce Connector', icon: '🌐', permission: ['woocommerce.syc_categories', 'woocommerce.sync_products', 'woocommerce.sync_orders', 'woocommerce.map_tax_rates', 'woocommerce.access_woocommerce_api_settings'] },
        { id: 'plaid', label: 'Plaid Banking', icon: '🏦', permission: 'plaid_banking.access' },
      ]
    },
    {
      title: 'AI Tools',
      items: [
        { id: 'ai-assistant', label: 'AI Assistant', icon: '🤖', permission: null },
      ]
    }
  ];

  const location = useLocation();
  const navigate = useNavigate();

  // Scroll to top whenever route changes
  useEffect(() => {
    if (contentWrapperRef.current) {
      contentWrapperRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Update active tab based on URL synchronously to prevent flash
  React.useLayoutEffect(() => {
    const pathParts = location.pathname.split('/').filter(p => p);
    const firstPath = pathParts[0] || 'home';

    const cleanPath = location.pathname.replace(/\/$/, ''); // Remove trailing slash
    if (cleanPath === '/sell' || cleanPath.startsWith('/sell/')) {
      setActiveTab('sell');
    } else if (firstPath) {
      setActiveTab(firstPath);
    } else if (firstPath === '' || cleanPath === '') {
      setActiveTab('home');
    }
  }, [location]);

  const handleTabChange = (tabId, customPath) => {
    setActiveTab(tabId);
    // Only close sidebar on mobile (screen width <= 768px)
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }

    // Use custom path if provided, otherwise use default navigation
    if (customPath) {
      navigate(customPath);
    } else if (tabId === 'home') {
      navigate('/home');
    } else {
      navigate(`/${tabId}`);
    }
  };

  const toggleSubmenu = (menuId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const renderContent = () => {
    // If we're on a nested route for users, render the outlet
    if (location.pathname.includes('users/') ||
      location.pathname.includes('products/') ||
      location.pathname.includes('roles/') ||
      location.pathname.includes('contacts/') ||
      location.pathname.includes('sales-commission-agents/') ||
      location.pathname.startsWith('/ai-assistant') ||
      location.pathname === '/profile') {
      return <Outlet />;
    }

    // Otherwise render the tab content as before
    switch (activeTab) {
      case 'home': return <Home currentUser={currentUser} />;
      case 'users':
      case 'roles':
      case 'agents':
        return <Users activeTab={activeTab} />;
      case 'contacts':
      case 'suppliers':
      case 'customers':
      case 'customer_groups':
      case 'import_contacts':
        return <Contacts activeTab={activeTab} />;
      case 'products':
      case 'all_products':
      case 'stock_report':
      case 'units':
      case 'warranties':
      case 'brands':
      case 'update_price':
      case 'variation':
      case 'import_product':
      case 'categories':
      case 'selling_price_group':
        return <Products activeTab={activeTab} />;
      case 'purchases':
      case 'list_purchases':
      case 'purchase_order':
      case 'purchase_requisition':
      case 'purchase_return':
        return <Purchases activeTab={activeTab} />;
      case 'add-purchase': return <AddPurchase />;
      case 'sell':
      case 'all_sales':
      case 'sales_returns':
      case 'shipment':
      case 'discount':
      case 'list_quotation':
      case 'list_draft':
        return <Sell activeTab={activeTab} />;
      case 'stock-transfers':
      case 'list_transfers':
      case 'add_transfer':
        return <StockTransfers activeTab={activeTab} />;
      case 'stock-adjustment':
      case 'list_stock_adjustment':
      case 'add_stock_adjustment':
        return <StockAdjustment activeTab={activeTab} />;
      case 'expenses':
      case 'list_expenses':
      case 'expense_categories':
        return <Expenses activeTab={activeTab} />;
      case 'payment-accounts':
      case 'list_accounts':
      case 'account_types':
      case 'balance_sheet':
      case 'trial_balance':
      case 'cash_flow':
      case 'payment_report':
        return <PaymentAccounts activeTab={activeTab} />;
      case 'accounting': return <Accounting />;
      case 'reports': return <Reports />;
      case 'notification-templates': return <NotificationTemplates />;
      case 'settings': return <Settings />;
      case 'crm':
      case 'crm_dashboard':
      case 'crm_leads':
      case 'crm_followups':
      case 'crm_campaigns':
      case 'crm_contactslogin':
      case 'crm_commissions':
      case 'crm_reports':
      case 'crm_proposal_template':
      case 'crm_proposals':
      case 'crm_sources':
      case 'crm_life_stage':
      case 'crm_followup_category':
      case 'crm_settings':
        return <CRM activeTab={activeTab} />;
      case 'woocommerce': return <WoocommerceConnector />;
      case 'plaid': return <PlaidConnector />;
      // SuperAdmin routes
      case 'system-settings': return <SystemSettings />;
      case 'user-roles': return <UserRoles />;
      case 'business-management': return <BusinessManagement />;
      case 'system-monitoring': return <SystemMonitoring />;
      case 'audit-logs': return <AuditLogs />;
      case 'backup-restore': return <BackupRestore />;
      case 'ai-assistant': return <Outlet />;
      default: return <Home />;
    }
  };

  // Show loading screen until permissions are fully loaded
  if (permissionLoading || !currentUser) {
    return (
      <div className="etpos-dashboard" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 2rem',
            background: 'white',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2.5rem',
            animation: 'pulse 2s ease-in-out infinite'
          }}>
            🏪
          </div>
          <h2 style={{
            fontSize: '1.8rem',
            marginBottom: '1rem',
            fontWeight: '600'
          }}>
            ET-POS
          </h2>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            fontSize: '1.1rem',
            marginTop: '1.5rem'
          }}>
            <svg
              className="circular-arrow-spinner"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: '24px', height: '24px' }}
            >
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
            <span>Loading your workspace...</span>
          </div>
          <p style={{
            marginTop: '0.5rem',
            fontSize: '0.9rem',
            opacity: 0.9
          }}>
            Please wait while we load your permissions and business data
          </p>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="etpos-dashboard">
      {/* Header */}
      <header className="etpos-header">
        <div className="header-left">
          <button
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <h1 className="store-title">
            {(() => {
              const storeName = currentUser?.business?.name || currentUser?.business_name || 'ET-POS Store';
              // Extract first and last letters for abbreviation
              const words = storeName.trim().split(/\s+/);
              let abbreviation = '';
              if (words.length > 1) {
                // Multi-word: first letter of first word + first letter of last word
                const firstLetter = words[0].charAt(0).toUpperCase();
                const lastLetter = words[words.length - 1].charAt(0).toUpperCase();
                abbreviation = `${firstLetter}${lastLetter}`;
              } else {
                // Single word: first and last letter of the word
                const firstLetter = storeName.charAt(0).toUpperCase();
                const lastLetter = storeName.charAt(storeName.length - 1).toUpperCase();
                abbreviation = `${firstLetter}${lastLetter}`;
              }
              return (
                <>
                  <span className="store-title-full">{storeName}</span>
                  <span className="store-title-abbr">{abbreviation}</span>
                </>
              );
            })()}
          </h1>
          <div className="header-btn date-btn desktop-only-item" style={{ cursor: 'default', marginLeft: '1.5rem' }}>
            📅 <strong>{dateUtils.formatDate(new Date())}</strong>
          </div>
        </div>

        <div className="header-center">
          {/* Date filter moved to Home dashboard */}
        </div>

        <div className="header-right">
          <button
            className="header-btn pos-btn"
            onClick={() => navigate('/pos')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              fontWeight: '600'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
            POS
          </button>

          <div className="header-btn-dropdown-container" ref={todoDropdownRef} style={{ position: 'relative' }}>
            <button
              className="header-btn todo-btn"
              onClick={() => setTodoDropdownOpen(!todoDropdownOpen)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              ➕ <span className="todo-text">Add To Do</span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{
                  transform: todoDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <path d="M3 4.5L6 7.5L9 4.5" />
              </svg>
            </button>

            {todoDropdownOpen && (
              <div
                className="user-dropdown-menu"
                style={{
                  display: 'block',
                  top: '120%',
                  right: 'auto',
                  left: '0',
                  width: '200px',
                  marginTop: '0'
                }}
              >
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setTodoDropdownOpen(false);
                    setShowCalendar(!showCalendar);
                  }}
                >
                  📅 Calendar
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setTodoDropdownOpen(false);
                    setShowCalculatorPopup(!showCalculatorPopup);
                  }}
                >
                  🧮 Calculator
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => { console.log('Tour clicked'); setTodoDropdownOpen(false); }}
                >
                  🎯 Application Tour
                </button>

                <div className="dropdown-divider"></div>
                <button
                  className="dropdown-item"
                  onClick={() => { window.open('https://tawk.to/chat/66fd05ec4cbc4814f7e1de9e/1i965u9ab', '_blank'); setTodoDropdownOpen(false); }}
                >
                  🎧 Support
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => { setDarkMode(!darkMode); setTodoDropdownOpen(false); }}
                >
                  {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setTodoDropdownOpen(false);
                    setShowTodayProfitModal(true);
                    fetchTodayProfitData();
                  }}
                >
                  💰 Today's Profit
                </button>
              </div>
            )}

            {/* Mini Calendar Popup */}
            {showCalendar && (
              <div
                ref={calendarRef}
                style={{
                  position: 'absolute',
                  top: '120%',
                  right: '0',
                  zIndex: 1002,
                  background: 'white',
                  padding: '10px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  border: '1px solid #e2e8f0'
                }}
              >
                <DatePicker
                  selected={calendarDate}
                  onChange={(date) => setCalendarDate(date)}
                  inline
                />
              </div>
            )}

            {/* Calculator Popup */}
            {showCalculatorPopup && (
              <div ref={calculatorRef}>
                <Calculator onClose={() => setShowCalculatorPopup(false)} />
              </div>
            )}

            {/* Today's Profit Modal */}
            {showTodayProfitModal && (
              <div ref={todayProfitRef}>
                <TodayProfitModal
                  isOpen={showTodayProfitModal}
                  onClose={() => setShowTodayProfitModal(false)}
                  profitData={todayProfitData}
                />
              </div>
            )}
          </div>

          <div className="notification-section">
            <button className="notification-btn">
              🔔
              <span className="notification-badge">3</span>
            </button>
          </div>
          <div className="user-section" onClick={toggleUserDropdown} ref={userSectionRef}>
            <span className="user-name">
              {currentUser ?
                `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username || 'User'
                : 'Loading...'}
            </span>
            <div className="profile-logo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>

            {/* Dropdown Menu */}
            {userDropdownOpen && (
              <div className="user-dropdown-menu">
                <div className="dropdown-header-item">
                  <div className="signed-in-text">Signed in as</div>
                  <div className="signed-in-name">
                    {currentUser ?
                      `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username || 'User'
                      : 'User'}
                  </div>
                </div>
                <div className="dropdown-divider"></div>

                <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); navigate('/profile'); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Profile
                </button>

                <button className="dropdown-item logout-item" onClick={(e) => { e.stopPropagation(); handleLogout(); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="etpos-body">
        {/* Sidebar */}
        <aside className={`etpos-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-content" onScroll={() => setHoveredItem(null)}>
            {menuCategories.map((category, index) => {
              console.log(`🔐 RBAC DEBUG - Processing category: ${category.title}`);

              // Check if user has superadmin role
              const userRoles = currentUser?.roles || [];
              const isSuperAdmin = userRoles.some(role => {
                const roleName = role.name?.toLowerCase() || '';
                return roleName === 'superadmin' ||
                  roleName === 'super admin' ||
                  roleName === 'super_admin';
              });

              console.log(`🔐 RBAC DEBUG - Current user: ${currentUser?.username}`);
              console.log(`🔐 RBAC DEBUG - User roles:`, userRoles.map(r => r?.name));
              console.log(`🔐 RBAC DEBUG - Is Super Admin: ${isSuperAdmin}`);

              // Check if user is an Admin (matches web $is_admin logic)
              const isAdmin = userRoles.some(role => {
                const name = role.name || '';
                return name.toLowerCase().startsWith('admin') || name.toLowerCase() === 'administrator';
              });

              // Helper to check permission (string or array)
              // Admin users bypass permission checks for Sell menu (matches web $is_admin)
              const checkPerm = (perm, isSellItem = false) => {
                if (!perm) return true;
                if (isAdmin && isSellItem) return true;
                if (Array.isArray(perm)) return hasAnyPermission(perm);
                return hasPermission(perm);
              };

              // Filter items based on permissions, and also filter their subItems
              let filteredItems = category.items.map(item => {
                if (item.subItems) {
                  const isSellParent = item.id === 'sell';
                  const filteredSubItems = item.subItems.filter(sub => checkPerm(sub.permission, isSellParent));
                  return { ...item, subItems: filteredSubItems };
                }
                return item;
              }).filter(item => {
                if (item.subItems) {
                  return item.subItems.length > 0;
                }
                return checkPerm(item.permission);
              });

              // Filter Superadmin tab based on role
              // If user is NOT superadmin, hide the Superadmin tab
              if (!isSuperAdmin) {
                filteredItems = filteredItems.filter(item => item.id !== 'system-settings');
                console.log(`🔐 RBAC DEBUG - Non-Superadmin user - hiding Superadmin tab`);
              } else {
                console.log(`🔐 RBAC DEBUG - Superadmin user - showing all tabs including Superadmin`);
              }

              console.log(`🔐 RBAC DEBUG - Category ${category.title} - Filtered items count: ${filteredItems.length}`);

              // Only show category if it has visible items
              if (filteredItems.length === 0) {
                console.log(`🔐 RBAC DEBUG - Hiding category ${category.title} - no accessible items`);
                return null;
              }

              return (
                <div key={index} className="menu-category">
                  <div className="category-title">
                    {category.title}
                    {category.title === 'Main' && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: '600', color: '#f59e0b' }}>V6.4</span>}
                  </div>
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className="nav-item-wrapper"
                      data-label={item.label}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoveredItem({
                          id: item.id,
                          top: sidebarOpen ? (rect.top + rect.height / 2) : rect.top
                        });
                      }}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <button
                        className={`nav-item ${activeTab === item.id || (item.subItems && item.subItems.some(sub => sub.id === activeTab)) ? 'active' : ''}`}
                        onClick={(e) => {
                          if (item.subItems && window.innerWidth <= 768) {
                            e.preventDefault();
                            e.stopPropagation();
                            setClickedMenuId(clickedMenuId === item.id ? null : item.id);
                          } else {
                            if (item.subItems) {
                              handleTabChange(item.subItems[0].id);
                            } else {
                              handleTabChange(item.id, item.path);
                            }
                          }
                        }}
                      >
                        <span className="nav-icon">{item.icon}</span>
                        <span className="nav-label">{item.label}</span>
                      </button>
                      
                      {/* Mobile Inline Submenu Accordion */}
                      {item.subItems && clickedMenuId === item.id && window.innerWidth <= 768 && (
                        <div className="mobile-inline-submenu">
                          {item.subItems.map((subItem) => (
                            <button
                              key={subItem.id}
                              className={`nav-item mobile-submenu-item ${activeTab === subItem.id ? 'active' : ''}`}
                              onClick={() => handleTabChange(subItem.id)}
                              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                            >
                              {subItem.icon && (
                                <span className="submenu-icon-wrapper" style={{ display: 'inline-flex', alignItems: 'center', width: '20px', justifyContent: 'center' }}>
                                  {subItem.icon.startsWith('fa') ? (
                                    <i className={subItem.icon} style={{ fontSize: '0.9rem', opacity: 0.75 }}></i>
                                  ) : (
                                    subItem.icon
                                  )}
                                </span>
                              )}
                              <span className="nav-label">{subItem.label}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Desktop Hover Submenu */}
                      {item.subItems && (
                        <div
                          className="submenu-container"
                          style={
                            hoveredItem && hoveredItem.id === item.id
                              ? { top: `${hoveredItem.top}px` }
                              : {}
                          }
                        >
                          <div className="hover-submenu-header">{item.label}</div>
                          <div className="submenu-items">
                            {item.subItems.map((subItem) => (
                              <button
                                key={subItem.id}
                                className={`nav-item submenu-item ${activeTab === subItem.id ? 'active' : ''}`}
                                onClick={() => handleTabChange(subItem.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                              >
                                {subItem.icon && (
                                  <span className="submenu-icon-wrapper" style={{ display: 'inline-flex', alignItems: 'center', width: '20px', justifyContent: 'center' }}>
                                    {subItem.icon.startsWith('fa') ? (
                                      <i className={subItem.icon} style={{ fontSize: '0.9rem', opacity: 0.75 }}></i>
                                    ) : (
                                      subItem.icon
                                    )}
                                  </span>
                                )}
                                <span className="nav-label">{subItem.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <div className="sidebar-footer">
            <button className="sidebar-toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? '◀ Collapse Sidebar' : '▶ Expand'}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="etpos-main-content">
          <div className="content-wrapper" ref={contentWrapperRef}>
            {renderContent()}
          </div>
        </main>
      </div>

      {/* Secure AI Assistant floating widget */}
      <SecureAIAssistant />

      {/* Footer */}
      <footer className="etpos-footer">
        <span>et-pos - V6.4 | Copyright © 2025 All rights reserved.</span>
      </footer>
    </div>
  );
};

export default ETPOSDashboard;
